import { ElementType, useEffect, useState } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { MessagesHooks } from '@frontend/api-messaging';
import { SMSDataSharedUtils } from '@frontend/api-sms-data';
import { NoAccessEmptyState } from '@frontend/assets';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppFlagStore } from '@frontend/shared';
import { StateRefHelpers } from '@frontend/state-ref-helpers';
import { ThreadBodyComponents, ThreadBodyTypes } from '@frontend/thread-body';
import { ThreadHeader } from '@frontend/thread-header';
import type { LocationPhoneSelection, PersonPhoneSelection } from '@frontend/thread-header-context';
import { CommonHTMLAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { PolymorphicComponentPropWithoutRef, useFileUpload } from '@frontend/design-system';
import { ContextPreview, ContextPreviewProps } from '../../../common';
import { useThreadMedia } from '../../hooks';
import { ThreadBody, ThreadSendingArea } from '../thread-view';

export type SendInThreadStateProps = {
  imageUploadProps: ReturnType<typeof useFileUpload>;
  groupId: string;
  threadId: string;
  person?: Person;
  optedOut?: boolean;
  personInThreadContext?: Pick<Person, 'firstName' | 'lastName' | 'preferredName' | 'personId'>;
  selectedPersonPhone: string;
  onSelectPersonPhone: (selection: PersonPhoneSelection, threadId?: string) => void;
  selectedLocationPhone: LocationPhoneSelection;
  onSelectLocationPhone: (selection: LocationPhoneSelection, threadId?: string) => void;
  bodyValue: string;
  dynamicModalContextSubtitle?: string;
  setBodyValue: (value: string) => void;
  onSend: () => void;
  onSchedule: (scheduledTime: Date | string, pausable: boolean) => void;
  hideSelectTemplateButton?: boolean;
  isUnknownContactThread?: boolean;
} & Pick<
  ReturnType<typeof MessagesHooks.useThread>,
  | 'threadItems'
  | 'scheduledMessages'
  | 'isLoadingFirstPage'
  | 'hasOlderMessages'
  | 'mediaQueries'
  | 'fetchOlderMessages'
> &
  Pick<ReturnType<typeof useThreadMedia>, 'threadMedia' | 'removeMediaItem'>;

export type SendInThreadCustomizationProps = {
  bodyAndSendAreaContainerProps?: CommonHTMLAttributes & { css?: Interpolation<Theme> };
  contextPreviewProps?: ContextPreviewProps;
  hideSuperTextAreaSendButton?: boolean;
  onSelectTemplateClick?: () => void;
};

type PolymorphicProps<C extends ElementType> = PolymorphicComponentPropWithoutRef<
  C,
  SendInThreadStateProps & SendInThreadCustomizationProps
>;

export const SendInThreadView = <C extends ElementType = 'div'>({
  as,
  imageUploadProps,
  groupId,
  threadId,
  person,
  optedOut,
  selectedPersonPhone,
  personInThreadContext,
  onSelectPersonPhone,
  selectedLocationPhone,
  onSelectLocationPhone,
  bodyValue,
  setBodyValue,
  onSend,
  onSchedule,
  threadItems,
  scheduledMessages,
  isLoadingFirstPage,
  hasOlderMessages,
  mediaQueries,
  fetchOlderMessages,
  isUnknownContactThread,
  threadMedia,
  removeMediaItem,
  bodyAndSendAreaContainerProps,
  contextPreviewProps,
  dynamicModalContextSubtitle,
  hideSuperTextAreaSendButton,
  onSelectTemplateClick,
  hideSelectTemplateButton,
  ...rest
}: PolymorphicProps<C>) => {
  const { t } = useTranslation('integrated-messaging');
  const Component = as || 'div';
  const [hideSendingArea, setHideSendingArea] = useState(false);
  const { featureFlags, getFeatureFlagValue } = useAppFlagStore();
  const shouldUseLocationPhones = getFeatureFlagValue({
    locationIds: [groupId],
    flagName: 'phone-numbers-name-id',
    strategy: 'ALL',
  });
  const aclEnforcementEnabled = !!featureFlags[groupId]?.get('nwx:messaging-integrated-messaging-fe-acl-enforcement')
    ?.value;
  const showNoAccessState = aclEnforcementEnabled ? !hasSchemaACL(groupId, Permission.SMS_INBOX_MANAGE) : false;

  const manageSendingArea = (type: ThreadBodyTypes.onScheduleMessageActionType): void => {
    if (type === 'view_list') {
      setHideSendingArea(true);
    } else {
      setHideSendingArea(false);
    }
  };

  const disableSendingMessage = optedOut && !isLoadingFirstPage;

  const [previousThreadId] = StateRefHelpers.usePreviousValue(threadId);
  const { reportThreadClose } = SMSDataSharedUtils.useOpenThreadsShallowStore('reportThreadClose');

  const { getThreadSMSItems } = SMSDataSharedUtils.threadItemsUtils;
  const messages = getThreadSMSItems(threadItems);

  SMSDataSharedUtils.useReportTimeToOpenToPendo(messages[messages.length - 1], 'integrated-messaging');

  useEffect(() => {
    return () => {
      if (threadId && threadId !== previousThreadId) {
        reportThreadClose(threadId, 'integrated-messaging');
      }
    };
  }, [threadId, reportThreadClose]);

  if (showNoAccessState) {
    return <NoAccessEmptyState message={t('You do not have permission to view or send text messages')} />;
  }

  return (
    <Component {...imageUploadProps.dropContainerProps} {...rest}>
      <ThreadHeader
        css={{ minHeight: 'auto', height: 'auto' }}
        groupId={groupId}
        primaryContact={person}
        primaryContactId={person?.personId}
        hideAssociatedContacts
        personPhone={selectedPersonPhone}
        onPersonPhoneChange={onSelectPersonPhone}
        locationPhone={selectedLocationPhone}
        onLocationPhoneChange={onSelectLocationPhone}
        allowCustomPhoneNumber={true} // Toggle off to disable sending messages to custom numbers
        variant='responsive'
      >
        <ThreadHeader.Header css={{ alignItems: 'center' }}>
          <ThreadHeader.Title openContactPanel={false} variant='normal' />
          {contextPreviewProps && (
            <ContextPreview
              css={{
                maxWidth: '45%',
              }}
              {...contextPreviewProps}
              subtitle={dynamicModalContextSubtitle || contextPreviewProps.subtitle}
            />
          )}
        </ThreadHeader.Header>
        <ThreadHeader.SubHeader>
          <div css={{ display: 'flex' }}>
            <ThreadHeader.PersonPhoneSelector />
            {shouldUseLocationPhones ? (
              <ThreadHeader.LocationPhoneSelector hideCurrentLocationPhone />
            ) : (
              <ThreadHeader.DepartmentSelector />
            )}
          </div>
        </ThreadHeader.SubHeader>
      </ThreadHeader>
      <div {...bodyAndSendAreaContainerProps}>
        <ThreadBody
          threadItems={threadItems}
          scheduledMessages={[]}
          context={{
            person,
            personInThreadContext, // for when thread body loads messages from different contact (custom number) while the header and sending context doesn't change.
            threadIsLoading: isLoadingFirstPage,
            isNewThread: !isLoadingFirstPage && threadItems.length === 0,
            hasOlderMessages,
            optedOut,
            threadId: threadId ?? '',
            mediaQueries,
            isUnknownContactThread,
          }}
          fetchOlderMessages={fetchOlderMessages}
          css={{
            maxHeight: 280,
            minHeight: 280,
          }}
        />
        {disableSendingMessage && (
          <ThreadBodyComponents.OptOutBanner
            textProps={{
              size: 'medium',
            }}
            css={{
              margin: theme.spacing(0, 2, 2),
            }}
          />
        )}
        <ThreadBodyComponents.ScheduledMessageIndicator
          scheduledMessages={scheduledMessages}
          viewOnly
          threadId={threadId ?? ''}
          onAction={manageSendingArea}
        />
        {!hideSendingArea && (
          <ThreadSendingArea
            bodyValue={bodyValue}
            css={{ minHeight: 'auto', height: 'auto' }}
            setBodyValue={setBodyValue}
            disabled={disableSendingMessage}
            fileUploadProps={imageUploadProps}
            threadMedia={threadMedia}
            removeMediaItem={removeMediaItem}
            onSubmit={onSend}
            hideSubmitButton={hideSuperTextAreaSendButton}
            extraActions={
              !!onSelectTemplateClick && !hideSelectTemplateButton
                ? [
                    {
                      icon: (
                        <Icon
                          name='template'
                          css={{
                            color:
                              selectedPersonPhone && !disableSendingMessage
                                ? theme.colors.primary50
                                : theme.colors.neutral20,
                          }}
                        />
                      ),
                      label: t('Select a Template'),
                      showLabelAlways: true,
                      showLabelOnHover: false,
                      css: {
                        fontSize: theme.font.size.medium,
                        fontWeight: theme.font.weight.semibold,
                        color: theme.colors.primary50,
                      },
                      onClick: onSelectTemplateClick,
                      disabled: !selectedPersonPhone || disableSendingMessage,
                    },
                  ]
                : []
            }
          />
        )}
      </div>
    </Component>
  );
};
